/**
 * useAdvance — the one way the flow moves forward.
 *
 * Runs an api-client mutation, refreshes the server context so the guards see the new status, then
 * navigates to the next step's URL. Backward/lateral navigation is just links; only forward
 * transitions go through here (after a successful mutation), which keeps the step guards honest.
 */
import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { RefereeContext } from '@/lib/api-types';
import type { RefereeStep } from './steps';
import { refereeContextQueryKey } from './useSession';

/** A mutation that already resolved to the fresh context lets us seed the cache without a refetch. */
function isRefereeContext(value: unknown): value is RefereeContext {
  return (
    typeof value === 'object' &&
    value !== null &&
    'status' in value &&
    'candidate' in value &&
    'referee' in value
  );
}

export function useAdvance() {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const advance = useCallback(
    async (mutate: () => Promise<unknown>, next: RefereeStep) => {
      setPending(true);
      setError(null);
      try {
        const result = await mutate();
        const key = refereeContextQueryKey(token);
        // Seed the fresh context straight into the cache when the mutation already resolved it —
        // saves a refetch, and (crucially) avoids re-reading a decline-revoked token that would 404.
        // Otherwise invalidate so the guards still see the new server status.
        if (isRefereeContext(result)) {
          queryClient.setQueryData(key, result);
        } else {
          await queryClient.invalidateQueries({ queryKey: key });
        }
        navigate(`/r/${token}/${next}`);
      } catch (err) {
        setError(err);
      } finally {
        setPending(false);
      }
    },
    [token, navigate, queryClient],
  );

  return { advance, pending, error };
}
